import React from "react"
import {twMerge} from "tailwind-merge"

import {pickProps} from "../../utils/types"
import {FieldErrorMessage} from "./components"

export type TFieldLabelProps = {
  name: string
  label: React.ReactNode
  legend?: React.ReactNode
  required?: boolean
  hideError?: boolean
  labelClassName?: string
  containerClassName?: string
  errorMessageProps?: Partial<React.ComponentProps<typeof FieldErrorMessage>>
  children?: React.ReactNode
  variant?: "big" | "medium" | "small"
}

export const FieldLabel: React.FC<TFieldLabelProps> = ({
  name,
  required,
  label,
  legend,
  hideError,
  labelClassName,
  containerClassName,
  children,
  errorMessageProps,
  variant = "big",
}) => {
  return (
    <div className={twMerge(["flex flex-col gap-2 text-left", containerClassName])}>
      <Label
        variant={variant}
        className={labelClassName}
        required={required}
        legend={legend}
        htmlFor={name}
        legendClassName={"-mt-2"}
      >
        {label}
      </Label>
      <div>{children}</div>
      {!hideError && <FieldErrorMessage name={name} reserveSpace {...errorMessageProps} />}
    </div>
  )
}

type TLabelProps = Pick<TFieldLabelProps, "legend" | "required" | "variant"> &
  React.ComponentProps<"label"> & {legendClassName?: string}

export const Label: React.FC<TLabelProps> = ({required, className, legend, variant, legendClassName, ...props}) => (
  <>
    <label
      {...props}
      className={twMerge([
        required && "required-after",
        variant === "big" && "text-lg font-semibold",
        variant === "medium" && "text-base font-semibold",
        variant === "small" && "-mb-1.5 text-sm font-normal",
        className,
      ])}
    ></label>
    <legend className={twMerge("text-sm font-normal text-cr-grey-50", legendClassName)}>{legend}</legend>
  </>
)

export const pickFieldLabelProps = pickProps<Omit<TFieldLabelProps, "children">>({
  name: true,
  hideError: true,
  label: true,
  legend: true,
  required: true,
  errorMessageProps: true,
  labelClassName: true,
  containerClassName: true,
  variant: true,
})
